'use client'

import { Button, List } from '~/components/component-library'
import { DepositPanel } from '~/components/forms/deposit/DepositPanel'
import { StepDone } from '~/components/post-onboarding-checklist/StepDone'
import { WorkspaceAutoEarnStepAction } from '~/components/post-onboarding-checklist/WorkspaceAutoEarnStepAction'
import {
  getTeammateStepMeta,
  WAITING_FOR_INVITATION,
} from '~/components/post-onboarding-checklist/teammateStep'
import { isChecklistStepComplete } from '~/components/post-onboarding-checklist/utils'
import { InviteMemberDialogContent } from '~/components/settings/InviteMemberDialogContent'
import { ApproveTransactionDialogContent } from '~/components/smart-accounts/ApproveTransactionDialogContent'
import { useAuth } from '~/context/AuthProvider'
import { useActiveOrg, useActiveOrgMember } from '~/hooks/org'
import { useKycStatus } from '~/hooks/useKycStatus'
import { useOrgTransaction } from '~/hooks/useTransactions'
import {
  OrgSmartAccount,
  OrgTransaction,
  PostOnboardingChecklist,
  PostOnboardingChecklistStepKey,
} from '~/types'

const getTeammateStepTitle = ({
  isTeammateAdded,
  hasPendingInvitation,
}: {
  isTeammateAdded: boolean
  hasPendingInvitation: boolean
}) => {
  if (!isTeammateAdded && hasPendingInvitation) return 'Invitation sent'
  return 'Add a teammate'
}

const getTeammateStepDescription = ({
  isTeammateAdded,
  hasPendingInvitation,
}: {
  isTeammateAdded: boolean
  hasPendingInvitation: boolean
}) => {
  if (isTeammateAdded) return 'Teammate added'
  if (hasPendingInvitation) return WAITING_FOR_INVITATION
  return 'Who else should see or approve payments?'
}

const ReviewButton = ({ transaction }: { transaction?: OrgTransaction }) => {
  const { setGlobalModalState, closeGlobalModal } = useAuth()

  return (
    <Button
      variant="secondary"
      size="small"
      isLoading={!transaction}
      onClick={() => {
        if (!transaction) return
        setGlobalModalState({
          isOpen: true,
          content: (
            <ApproveTransactionDialogContent
              transaction={transaction}
              onClose={closeGlobalModal}
              skipRoutePath
            />
          ),
        })
      }}
    >
      Review
    </Button>
  )
}

const WorkspaceAutoEarnReviewButton = ({
  transactionId,
}: {
  transactionId: string
}) => {
  const { transaction } = useOrgTransaction(transactionId)
  return <ReviewButton transaction={transaction} />
}

// The full-page welcome state on Home. Shows while the checklist exists, FUND
// is incomplete, and the checklist is not hidden. The backend computes step
// completion; this component only renders it.
export const PostOnboardingChecklistView = ({
  checklist,
  treasuryAccount,
}: {
  checklist: PostOnboardingChecklist
  treasuryAccount: OrgSmartAccount
}) => {
  const { org } = useActiveOrg()
  const { isKycStarted } = useKycStatus()
  const { setGlobalModalState, closeGlobalModal } = useAuth()
  const orgMember = useActiveOrgMember()
  const isOwner = orgMember?.role === 'OWNER'

  const isTeammateAdded = isChecklistStepComplete(
    checklist,
    PostOnboardingChecklistStepKey.AddTeammate,
  )
  const hasPendingInvitation = !isTeammateAdded && !!org?.invitations.length
  const isAutoEarnOn = isChecklistStepComplete(
    checklist,
    PostOnboardingChecklistStepKey.AutoEarn,
  )

  const openInviteDialog = () => {
    setGlobalModalState({
      isOpen: true,
      content: <InviteMemberDialogContent closeDialog={closeGlobalModal} />,
    })
  }

  return (
    <div className="mx-auto flex h-full max-w-lg flex-grow flex-col space-y-12">
      <div className="w-full space-y-4">
        <h1 className="text-3xl">Welcome, {org!.name}</h1>
        <p>
          Your workspace is set up and ready to go. Get started by funding it
          from another wallet or connecting your bank.
        </p>
      </div>
      <div className="space-y-4 rounded-lg border border-default p-4">
        <div>
          <div className="font-medium">Fund your treasury</div>
          <p className="text-sm text-gray-500">Send crypto or connect a bank</p>
        </div>
        <DepositPanel
          account={treasuryAccount}
          defaultTab={isKycStarted ? 'fiat' : 'crypto'}
          showWalletDeposit
          showAskForPayment
        />
      </div>
      <List
        items={[
          {
            key: 'add-teammate',
            content: getTeammateStepTitle({
              isTeammateAdded,
              hasPendingInvitation,
            }),
            description: getTeammateStepDescription({
              isTeammateAdded,
              hasPendingInvitation,
            }),
            meta: getTeammateStepMeta({
              isTeammateAdded,
              hasPendingInvitation,
              isOwner,
              openInviteDialog,
            }),
          },
          {
            key: 'auto-earn',
            content: 'Earn on idle cash',
            description: isAutoEarnOn
              ? 'Earn automation is on'
              : 'Automatically earn interest on USDC in your workspace',
            meta: isAutoEarnOn ? (
              <StepDone />
            ) : (
              <WorkspaceAutoEarnStepAction
                pendingAction={(transactionId) => (
                  <WorkspaceAutoEarnReviewButton
                    transactionId={transactionId}
                  />
                )}
              />
            ),
          },
        ]}
      />
    </div>
  )
}
